import { buildRolePermissionTreeGroups } from "./rolePermissionTree";

/** Short UK English "can…" phrases for the Role Management summary. */
const ROLE_CAN_PHRASES = {
  USER_MANAGEMENT: "add and look after staff accounts",
  SPONSOR_CRO_MANAGEMENT:
    "manage Sponsor/CRO users, study sharing, and their credentials",
  CHANGE_CREDENTIALS: "reset another person's password or access code",
  ROLE_MANAGEMENT: "create roles and choose what each role can do",
  SITE_MANAGEMENT: "add and look after clinical sites",
  DEPARTMENT_MANAGEMENT: "add and look after departments",
  CAPACITY_MANAGEMENT: "set clinic capacity",
  CAPA_CONFIGURE: "set up how CAPA works for this organisation",
  QMS_CONFIGURE: "set up QMS for this organisation",
  EISF_CONFIGURE: "set up eISF for this organisation",
  ADMIN_DASHBOARD: "see the admin dashboard",

  STUDY_LIST: "see live studies",
  ARCHIVED_STUDY_LIST: "see archived studies",
  ADD_STUDY: "add a new study",
  EDIT_STUDY: "change study details",
  ADD_EDIT_SCHEDULE: "create and change visit schedules",
  CHANGE_STATUS: "change a study's status",

  ISF_DASHBOARD: "see the eISF dashboard",
  ISF_REPORTS: "run eISF reports",
  VIEW_ISF: "open and read eISF files",
  CREATE_ISF: "create an eISF for a study",
  CREATE_FOLDER: "add folders in eISF",
  CREATE_SUB_FOLDER: "add sub-folders in eISF",
  UPDATE_FOLDER: "rename eISF folders",
  MOVE_FOLDER: "move eISF folders",
  REMOVE_FOLDER: "remove eISF folders",
  GENERATE_CERTIFICATION: "generate eISF certificates",
  CREATE_STUDY_ADHOC_PLACEHOLDER: "add extra placeholders on a study eISF",
  EDIT_STUDY_ADHOC_PLACEHOLDER: "edit those extra placeholders",
  REMOVE_STUDY_ADHOC_PLACEHOLDER: "remove those extra placeholders",
  FOLDER_LIBRARY: "look after the folder library",
  MILESTONE_LIBRARY: "look after the milestone library",
  MILESTONE_PLAN: "look after milestone plans",
  TEMPLATE_MANAGER: "look after eISF templates",
  AUDIT_LOG: "see and download the eISF audit log",

  VIEW_CAPA: "see the CAPA list",
  ADD_CAPA: "create a CAPA",
  VIEW_REMOVED_CAPA: "see removed CAPA records",
  CAPA_REPORTS: "see CAPA reports",
  CAPA_AUDIT_LOG: "see the CAPA audit log",

  BOOK_APPOINTMENT: "book appointments",
  CANCEL_APPOINTMENT: "cancel appointments",
  RESCHEDULE_APPOINTMENT: "reschedule appointments",
  CHANGE_APPOINTMENT_STATUS: "change appointment status",
  VIEW_APPOINTMENT_LIST: "see the appointment list",
  VIEW_APPOINTMENT_CALENDAR: "see the appointment calendar",
  VIEW_LOCATION_OCCUPANCY: "see location occupancy",
  VIEW_APPOINTMENT_CHANGES: "see appointment changes",
  VIEW_EPLANNER_ANALYTICS: "see E-Planner analytics",
  VIEW_WHATSAPP_COMMUNICATION: "see and reply to WhatsApp messages",

  PARTICIPANT_MANAGER: "open Participant Manager and add participants",
  CAMPAIGN_TEMPLATES: "create campaign templates",
  FEASIBILITY_MANAGER: "use Feasibility Manager",
  SEND_BULK_EMAIL_SMS: "send bulk email and text messages",

  QMS_VAULT: "work on their own QMS documents in My Vault",
  QMS_CAN_REVIEW_DOCUMENTS: "review QMS documents",
  QMS_CAN_APPROVE_DOCUMENTS: "approve QMS documents",
  QMS_PUBLISH_DOCUMENTS: "publish QMS documents",
  QMS_DOCUMENT_LIBRARY: "open the QMS document library",
  QMS_LIBRARY_SEE_STAFF: "assign staff to library documents",
  QMS_LIBRARY_ADD_STAFF: "assign staff to My Learning",
  QMS_QUIZ_MANAGER: "create and manage quizzes",
  QMS_DOCUMENT_CONTROL: "archive documents and change document owners",
};

function phraseFor(permission) {
  const fromMap = ROLE_CAN_PHRASES[permission.key];
  if (fromMap) return fromMap;
  const label = String(permission.label || permission.key || "")
    .trim()
    .replace(/^Can\s+/i, "")
    .toLowerCase();
  return label || "use this feature";
}

function quoteRoleName(name) {
  const trimmed = String(name || "").trim();
  return trimmed ? `the ${trimmed} role` : "this role";
}

function asListItem(phrase) {
  const text = String(phrase || "").trim();
  if (!text) return "";
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
}

function summariseSection(section) {
  const { granted, missing, total } = section;
  const grantedItems = granted.map(phraseFor).map(asListItem);
  const missingItems = missing.map(phraseFor).map(asListItem);
  const grantedCount = granted.length;

  if (grantedCount === total) {
    return {
      ...section,
      mode: "full",
      headline: "Full access to this area.",
      items: [],
    };
  }

  if (total >= 5 && missing.length > 0 && missing.length <= 2) {
    return {
      ...section,
      mode: "almost",
      headline: "Almost full access. They cannot:",
      items: missingItems,
    };
  }

  if (grantedCount === 1) {
    return {
      ...section,
      mode: "list",
      headline: "",
      items: grantedItems,
    };
  }

  return {
    ...section,
    mode: "list",
    headline: "",
    items: grantedItems,
  };
}

export function buildRolePermissionSummary({
  userData,
  selectedPermissions = [],
  roleName = "",
  isFullAccess = false,
} = {}) {
  const roleLabel = quoteRoleName(roleName);

  if (isFullAccess) {
    return {
      intro: `People with ${roleLabel} can use every part of the system.`,
      note: "These permissions cannot be changed.",
      sections: [],
      empty: false,
    };
  }

  const selected = new Set(
    (selectedPermissions || []).map((key) => String(key)),
  );
  const groups = buildRolePermissionTreeGroups(userData);
  const sections = groups
    .map((group) => {
      const permissions = group.permissions || [];
      const granted = permissions.filter((item) => selected.has(item.key));
      if (!granted.length) return null;
      return summariseSection({
        id: group.id,
        label: group.label,
        icon: group.icon,
        color: group.color,
        granted,
        missing: permissions.filter((item) => !selected.has(item.key)),
        total: permissions.length,
        grantedCount: granted.length,
      });
    })
    .filter(Boolean);

  if (!sections.length) {
    return {
      intro: `People with ${roleLabel} cannot do anything yet.`,
      note: "Tick the boxes to choose what this role can do.",
      sections: [],
      empty: true,
    };
  }

  return {
    intro: `People with ${roleLabel} can:`,
    note: "",
    sections,
    empty: false,
  };
}
